"use client";

import { useEffect, type RefObject } from "react";

interface CodeCopyProps {
  containerRef: RefObject<HTMLDivElement | null>;
}

export default function CodeCopy({ containerRef }: CodeCopyProps) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preBlocks = container.querySelectorAll("pre");
    if (preBlocks.length === 0) return;

    const cleanupFns: (() => void)[] = [];

    preBlocks.forEach((pre) => {
      if (pre.querySelector("[data-code-header]")) return;

      const codeEl = pre.querySelector("code");
      const code = codeEl?.textContent || "";
      const codeClass = codeEl?.className || "";
      const langMatch = codeClass.match(/language-(\w+)/);
      let lang = langMatch ? langMatch[1] : "";

      if (!lang) {
        if (code.includes("import ") || code.includes("def ") || code.includes("print(")) lang = "python";
        else if (code.includes("const ") || code.includes("function ") || code.includes("=>")) lang = "javascript";
        else if (code.includes("curl ")) lang = "bash";
        else if (code.includes("SELECT ") || code.includes("INSERT ")) lang = "sql";
        else if (code.includes("package ") || code.includes("func ")) lang = "go";
      }

      // Wrap the <pre> in a positioned container so the header can sit
      // above the scrollable code area. This makes the copy button
      // always visible regardless of code width or scroll position.
      const wrapper = document.createElement("div");
      wrapper.setAttribute("data-code-wrapper", "true");
      wrapper.className = "relative not-prose";

      const header = document.createElement("div");
      header.setAttribute("data-code-header", "true");
      header.className =
        "absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 h-9 pointer-events-none";

      const langSpan = document.createElement("span");
      langSpan.className =
        "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-black/40 text-white/70 backdrop-blur-sm pointer-events-none";
      langSpan.textContent = lang || "Code";

      const copyBtn = document.createElement("button");
      copyBtn.type = "button";
      copyBtn.setAttribute("aria-label", "Copy code to clipboard");
      copyBtn.className =
        "pointer-events-auto flex items-center gap-1.5 px-2.5 h-7 rounded-md text-[11px] font-semibold bg-black/60 hover:bg-black/80 text-white/80 hover:text-white backdrop-blur-sm border border-white/10 transition-colors";
      copyBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg><span>Copy</span>`;

      const setState = (state: "idle" | "copied" | "failed") => {
        if (state === "copied") {
          copyBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg><span class="text-emerald-400">Copied</span>`;
        } else if (state === "failed") {
          copyBtn.innerHTML = `<span class="text-red-400">Failed</span>`;
        } else {
          copyBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg><span>Copy</span>`;
        }
      };

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(code);
          setState("copied");
          setTimeout(() => setState("idle"), 2000);
        } catch {
          try {
            const textarea = document.createElement("textarea");
            textarea.value = code;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            setState("copied");
            setTimeout(() => setState("idle"), 2000);
          } catch {
            setState("failed");
            setTimeout(() => setState("idle"), 2000);
          }
        }
      };

      copyBtn.addEventListener("click", handleCopy);

      header.appendChild(langSpan);
      header.appendChild(copyBtn);

      // Replace pre with wrapper containing pre + header
      pre.parentNode?.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);
      wrapper.appendChild(header);

      // Make pre positioned and add top padding so code doesn't sit under the header
      pre.style.position = "relative";
      pre.style.paddingTop = "2.75rem";
      pre.style.borderRadius = "0.875rem";

      cleanupFns.push(() => {
        copyBtn.removeEventListener("click", handleCopy);
        // Move pre back to its original parent
        if (pre.parentNode === wrapper) {
          wrapper.parentNode?.insertBefore(pre, wrapper);
        }
        header.remove();
        wrapper.remove();
      });
    });

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [containerRef]);

  return null;
}
